// Page arithmetic for a horizontally scroll-snapped carousel track.
//
// The track is a flex row of equal-width items separated by a fixed gap, sized
// in CSS so that a whole number of items fills the visible width. How many that
// is changes with the breakpoint and is never passed in here: every answer is
// derived from the measured geometry, so the same math holds at every size.
//
// Everything takes and returns plain numbers, so none of it needs a DOM.

// Measurements come straight off the DOM, where a not-yet-laid-out element
// reports 0 and a detached one can report NaN. Treating anything that is not a
// real number as 0 keeps a single bad read from poisoning the arithmetic.
const toFinite = value => (Number.isFinite(value) ? value : 0);

const atLeastZero = value => Math.max(0, toFinite(value));

/**
 * The pixel distance between the start of one page and the start of the next.
 *
 * With `perPage` items of width W and gap G, the visible width is
 * `perPage * W + (perPage - 1) * G` while item k starts at `k * (W + G)`.
 * Adding one gap to the visible width closes that difference, so a page of
 * scrolling always lands flush on the start of an item.
 *
 * Returns 0 for a track that has not been laid out yet.
 */
export const pageStride = ({ clientWidth, gap } = {}) => {
  const width = atLeastZero(clientWidth);

  return width === 0 ? 0 : width + atLeastZero(gap);
};

/** How far the track can scroll before it runs out of content. */
export const maxScrollLeft = ({ scrollWidth, clientWidth } = {}) =>
  Math.max(0, toFinite(scrollWidth) - toFinite(clientWidth));

/**
 * How many pages the track holds, counting a short final page as a full one.
 * Always a finite integer of at least 1, so the dots never vanish or explode.
 */
export const pageCount = ({ scrollWidth, clientWidth, gap } = {}) => {
  const stride = pageStride({ clientWidth, gap });
  if (stride <= 0) return 1;

  return Math.ceil(maxScrollLeft({ scrollWidth, clientWidth }) / stride) + 1;
};

/**
 * Which page a given scroll position is showing, as an index in
 * [0, pageCount - 1].
 *
 * A final page holding fewer items than a full one cannot be scrolled a whole
 * stride, so plain rounding would keep reporting the page before it. Reaching
 * the end of the track therefore reports the last page outright. The one-pixel
 * tolerance absorbs the sub-pixel shortfall browsers leave behind on a
 * fractional layout.
 */
export const pageFromScroll = ({
  scrollLeft,
  scrollWidth,
  clientWidth,
  gap,
} = {}) => {
  const count = pageCount({ scrollWidth, clientWidth, gap });
  const stride = pageStride({ clientWidth, gap });
  if (count <= 1 || stride <= 0) return 0;

  const end = maxScrollLeft({ scrollWidth, clientWidth });
  const position = Math.min(atLeastZero(scrollLeft), end);
  if (position >= end - 1) return count - 1;

  return Math.min(Math.max(Math.round(position / stride), 0), count - 1);
};

/**
 * Where to scroll to put a page at the start of the viewport, clamped so the
 * last page settles against the end of the track instead of overshooting it.
 */
export const scrollLeftForPage = (
  page,
  { scrollWidth, clientWidth, gap } = {}
) =>
  Math.min(
    Math.round(atLeastZero(page)) * pageStride({ clientWidth, gap }),
    maxScrollLeft({ scrollWidth, clientWidth })
  );

/**
 * The distance between the start of one card and the start of the next.
 *
 * Paging is the right unit for the dots, but the wrong one for settling a
 * drag: rounding a released drag to a page boundary discards anything shorter
 * than half a page, so the row cannot be nudged along by a single card. These
 * three settle on a card instead, which is also where CSS puts its snap
 * points, so a drag lands exactly where a wheel or a swipe would.
 *
 * Returns 0 for a track that has not been laid out yet.
 */
export const cardStride = ({ itemWidth, gap } = {}) => {
  const width = atLeastZero(itemWidth);

  return width === 0 ? 0 : width + atLeastZero(gap);
};

/** Which card a given scroll position sits nearest, as an index from 0. */
export const cardFromScroll = ({ scrollLeft, itemWidth, gap } = {}) => {
  const stride = cardStride({ itemWidth, gap });
  if (stride <= 0) return 0;

  return Math.max(0, Math.round(atLeastZero(scrollLeft) / stride));
};

/**
 * Where to scroll to put a card at the start of the viewport, clamped so the
 * last one settles against the end of the track instead of overshooting it.
 */
export const scrollLeftForCard = (
  card,
  { scrollWidth, clientWidth, itemWidth, gap } = {}
) =>
  Math.min(
    Math.round(atLeastZero(card)) * cardStride({ itemWidth, gap }),
    maxScrollLeft({ scrollWidth, clientWidth })
  );

/** The page after this one, wrapping back to the first past the last. */
export const nextPage = (current, count) => {
  const total = Math.floor(atLeastZero(count));
  if (total <= 1) return 0;

  return (Math.floor(atLeastZero(current)) + 1) % total;
};
